import {
  ALL_AVAILABLE_LANGUAGES,
  AvailableLanguages,
  DEFAULT_LOCALE,
  EntityId,
  PrintServiceProductImageEntityHydrated,
} from "@jackfruit/common"
import { PageType, ProductPageEntity } from "~/interfaces/entities/ProductPage"

export const photoBookHasWindowCover = (
  productImages: PrintServiceProductImageEntityHydrated[]
) => {
  const hasCover = productImages.some(image => {
    return image.metaData?.photoBook?.frameType === "windowCover"
  })

  return hasCover
}

export const photoBookHasSimpleCover = (
  productImages: PrintServiceProductImageEntityHydrated[]
) => {
  const hasCover = productImages.some(image => {
    return image.metaData?.photoBook?.frameType === "cover"
  })

  return hasCover
}

export const photoBookHasCustomCover = (
  productImages: PrintServiceProductImageEntityHydrated[]
) => {
  const hasCover = productImages.some(image => {
    return image.metaData?.photoBook?.frameType === "customCover"
  })

  return hasCover
}

export const photoBookFindImageForPage = (
  productPage: ProductPageEntity,
  productImages: PrintServiceProductImageEntityHydrated[],
  productPageIds: EntityId[],
  language: AvailableLanguages
) => {
  const hasSimpleCover = photoBookHasSimpleCover(productImages)
  const hasWindowCover = photoBookHasWindowCover(productImages)
  const hasCustomCover = photoBookHasCustomCover(productImages)

  const hasCover = hasSimpleCover || hasWindowCover || hasCustomCover

  const currentPageIndex = productPageIds.indexOf(productPage.id)

  let foundPageImage: PrintServiceProductImageEntityHydrated | undefined

  switch (productPage.pageType) {
    case PageType.FrontCover:
      if (hasSimpleCover) {
        foundPageImage = productImages.find(image => {
          const coverImageLocale = image?.metaData?.locale || DEFAULT_LOCALE

          if (!ALL_AVAILABLE_LANGUAGES.includes(coverImageLocale)) {
            console.warn(
              `Invalid locale ${coverImageLocale}. List locales: ${ALL_AVAILABLE_LANGUAGES}`
            )
          }

          return (
            image.metaData?.photoBook?.frameType === "cover" &&
            language === coverImageLocale
          )
        })

        // If no foundPageImage, fallback to checking frameType
        if (!foundPageImage) {
          foundPageImage = productImages.find(
            image => image.metaData?.photoBook?.frameType === "cover"
          )
        }
      }
      if (hasWindowCover) {
        foundPageImage = productImages.find(image => {
          return image.metaData?.photoBook?.frameType === "windowCover"
        })
      }
      if (hasCustomCover) {
        foundPageImage = productImages.find(image => {
          return image.metaData?.photoBook?.frameType === "customCover"
        })
      }
      break
    case PageType.FrontCoverInside:
      foundPageImage = productImages.find(image => {
        return image.metaData?.photoBook?.frameType === "insideCover"
      })
      break

    case PageType.Page:
      // 3 cases, leftPage, rightPage, windowFirstPage
      let isLeft = currentPageIndex % 2 === 0
      let isFirstPage = currentPageIndex === 0
      if (hasCover) {
        isLeft = currentPageIndex % 2 === 1
        isFirstPage = currentPageIndex === 2
      }

      if (isFirstPage && hasWindowCover) {
        foundPageImage = productImages.find(image => {
          return image.metaData?.photoBook?.frameType === "windowFirstPage"
        })
      } else if (isFirstPage && hasSimpleCover) {
        foundPageImage = productImages.find(image => {
          return image.metaData?.photoBook?.frameType === "rightPage"
        })
      } else if (isLeft) {
        foundPageImage = productImages.find(image => {
          return image.metaData?.photoBook?.frameType === "leftPage"
        })
      } else {
        foundPageImage = productImages.find(image => {
          return image.metaData?.photoBook?.frameType === "rightPage"
        })
      }
      break
    case PageType.BackCoverInside:
      foundPageImage = productImages.find(image => {
        return image.metaData?.photoBook?.frameType === "lastPage"
      })
      break
  }

  if (!foundPageImage) {
    console.warn("Could not find image for page", productPage, currentPageIndex)
  }

  return foundPageImage
}
